import React, { useState } from "react";
import { Text, Group, Stack, ScrollArea, Popover, Box } from "@mantine/core";
import Swal from "sweetalert2";

import SpaceMemberFiltersModel from '../../../../models/SpaceMemberFilters';
import SpaceFilterSettingModel from "../../../../models/SpaceFilterSetting";

import {
  ALL_FILTER_OPTIONS,
  ALL_FILTER_PROPERTIES,
  generateUniqueName,
  generateKeyFromName,
  getOptionKey
} from "../../../../utils/Filters";
import { displayEmoji } from "../../../../utils/Emoji";

export default function FilterPropertySelector({
  slug,
  type,
  allFilters,
  handlers,
  selectedProperty,
  setSelectedProperty,
  withinPortal,
  TargetComponent
}) {
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [filterUploading, setFilterUploading] = useState(false);

  const filteredOptions = ALL_FILTER_OPTIONS.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const FilterModel = type === "filter-members" ? SpaceMemberFiltersModel : SpaceFilterSettingModel;

  const handleAddProperty = (option) => {
    if (!option || !slug) return;

    const filter_name = generateUniqueName(option.label, allFilters);
    const filter_key = generateKeyFromName(filter_name);
    const option_key = getOptionKey(option.value);
    const isFile = ["image", "file"].includes(option.value);

    let visibility = type === "filter-members" ? ["card", "profile"] : ["creation", "card", "page"];
    if (isFile) {
      visibility = visibility.filter((item) => item !== "card");
    }

    const params = {
      name: filter_name,
      sort_order: allFilters.length,
      key: filter_key,
      format: option.value,
      options: option.options ? option.options : {
        [option_key]: ALL_FILTER_PROPERTIES[option.value]?.default_value || "",
        values: []
      },
      visibility: visibility,
      emojicon: option.emojicon
    };

    setFilterUploading(true);
    FilterModel.onCreate(
      slug,
      params,
      (data) => {
        setSearch("");
        setPopoverOpened(false);
        setFilterUploading(false);
        if (data?.id) {
          handlers.append(data);
          setSelectedProperty(data);
        }
      },
      (error) => {
        setFilterUploading(false);
        const errors = Array.isArray(error?.errors) ? error?.errors : [];
        const errorsMessages = errors.join(', ');
        Swal.fire("Error", errorsMessages, "error");
      }
    );
  };

  const onFilterUpdate = (id, params) => {
    FilterModel.onUpdate(
      slug,
      id,
      params,
      (data) => {
        if (data?.id) {
          handlers.setState((current) =>
            current.map((item) => item.id === data.id ? data : item)
          );
        }
      },
      (error) => {
        const errors = Array.isArray(error?.errors) ? error?.errors : [];
        const errorsMessages = errors.join(', ');
        Swal.fire("Error", errorsMessages, "error");
      }
    );
  };

  return (
    <Popover
      opened={popoverOpened}
      onClose={() => setPopoverOpened(false)}
      position="right"
      placement="start"
      spacing={0}
      gutter={0}
      width={260}
      radius="md"
      transitionDuration={0}
      zIndex={9999999}
      withinPortal={!!withinPortal}
      target={<TargetComponent onClick={() => setPopoverOpened(!popoverOpened)} />}
    >
      <ScrollArea style={{ height: "auto" }} scrollbarSize={8} py={10}>
        {/*
          <TextInput
            placeholder="Search property"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            m={10}
          />
        */}
        <Box sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[1]}`, width: "100%" })} mb={5}>
          <Text size="sm" color="grey" ml={10} mr={10} mb={10}>
            {"Please select the type of property you would like to add."}
          </Text>
        </Box>
        <Stack spacing={2}>
          {filteredOptions.map((option) => (
            <Group
              key={option.value}
              spacing="sm"
              ml={8}
              mr={8}
              onClick={() => {
                if (!filterUploading) {
                  if (selectedProperty?.id) {
                    const filter_name = selectedProperty.name;
                    const filter_key = generateKeyFromName(filter_name);
                    const option_key = getOptionKey(option.value);
                    const new_params = {
                      name: filter_name,
                      key: filter_key,
                      format: option.value,
                      options: option.options ? option.options : {
                        [option_key]: ALL_FILTER_PROPERTIES[option.value]?.default_value || "",
                        values: []
                      },
                    };
                    onFilterUpdate(selectedProperty.id, new_params);
                    setSelectedProperty({...selectedProperty, ...new_params});
                    setPopoverOpened(null);
                  } else {
                    handleAddProperty(option);
                  }
                }
              }}
              sx={{ cursor: "pointer", padding: 6, borderRadius: 4, '&:hover': { backgroundColor: '#f5f5f5' } }}
            >
              <Box sx={{ width: 16 }} align="center">
                {displayEmoji(option.emojicon, "grey")}
              </Box>
              <Text>{option.label}</Text>
            </Group>
          ))}
        </Stack>
      </ScrollArea>
    </Popover>
  );
}
